import { getUserSelectedMicDeviceId } from '../../base/settings/functions.web';
// Bundled effect is plain ES module JS without type declarations.
// @ts-ignore
import { NkfAecEffect, resetSharedAecEngine, setCalibrationDebugInfo } from './NkfAecEffect.js';
import { getRemoteFarEndMix } from './RemoteFarEndMix';
import logger from './logger';
import { showNotification } from '../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../notifications/constants';
import { setNkfAecReady, setNkfAecSettling } from '../../nkf-aec/actions';
import { runCalibrationOnce } from './runCalibration';
// Dropout warning: shown at most this often. The condition can persist for
// minutes and repeating the message would only add noise to a call the user
// already knows is rough.
const DROPOUT_NOTIFY_COOLDOWN_MS = 5 * 60 * 1000;
/** When the dropout warning was last shown. */
let _lastDropoutNotifyMs = 0;
// Module-level reference to the running effect so Prejoin.tsx can apply
// calibration results directly without going through sessionStorage.
let _activeEffect = null;
export function applyCalibrationToEffect(delaySamples) {
    if (_activeEffect) {
        const delayMs = delaySamples * 1000 / 16000;
        _activeEffect.setFarDelaySamples(delaySamples);
        setCalibrationDebugInfo(delaySamples, delayMs, null, 'prejoin');
        logger.info(`NKF-aec: calibration applied directly: delay=${delaySamples} samples (${delayMs.toFixed(1)} ms)`);
    }
    else {
        logger.warn('NKF-aec: applyCalibrationToEffect called but no active effect');
    }
}
export function getActiveNkfAecEffect() {
    return _activeEffect;
}
// User AEC ON/OFF toggle (true = OFF / raw-mic passthrough). Remembered at
// module scope so the choice survives effect (re)creation: it is applied to
// the live effect immediately when one exists, and re-applied to any effect
// built later by createNkfAecEffect().
let _pendingUserBypass = false;
/**
 * Sets the user-facing AEC ON/OFF state.
 *
 * @param {boolean} bypass - True to turn AEC OFF (pass the raw mic through).
 * @returns {void}
 */
export function setNkfAecBypass(bypass) {
    _pendingUserBypass = bypass;
    if (_activeEffect) {
        _activeEffect._userBypass = bypass;
    }
}
/**
 * Returns the current user-facing AEC OFF state (true = OFF).
 *
 * @returns {boolean}
 */
export function getNkfAecBypass() {
    return _activeEffect ? Boolean(_activeEffect._userBypass) : _pendingUserBypass;
}
// Last converged far-end delay (samples @ 16 kHz), persisted at module scope
// so it survives effect (re)creation. A camera on/off fires a browser
// `devicechange` → lib-jitsi-meet marks the mic track "ended" → the app
// re-runs getUserMedia and builds a fresh NkfAecEffect. Without this, every
// toggle reset the delay to 0 and the AEC had to re-converge from scratch
// (seconds of leaked echo, and it can't even re-converge while the far-end/AI
// is silent). Seeding a new effect from this value makes it start aligned.
let _lastConvergedDelaySamples = 0;
// Which microphone that delay was measured on. The round-trip delay and the
// Worker's adaptive filter both describe ONE speaker→mic path, so they may only
// be reused while that path is unchanged. Carrying them onto a different mic is
// what made echo cancellation fail outright after switching mics on the prejoin
// screen: the delay pointed at the old device and the filter was still tuned to
// its acoustics, so the echo never lined up and nothing could be removed.
let _lastConvergedMicId = '';
/**
 * Returns the last converged far-end delay (0 if never converged).
 *
 * @returns {number}
 */
export function getNkfAecConvergedDelay() {
    return _lastConvergedDelaySamples;
}
// RNNoise (noise-suppression) ON/OFF, chained INSIDE the AEC graph (AEC → NS →
// AGC). Default OFF. Mirrors the bypass pattern so the choice survives effect
// (re)creation: applied to the live effect immediately, and re-applied to any
// effect built later by createNkfAecEffect().
let _pendingNoiseSuppression = false;
/**
 * Engages or bypasses the RNNoise denoise stage that lives inside NKF-AEC.
 *
 * @param {boolean} enabled - True to denoise.
 * @returns {void}
 */
export function setNkfAecNoiseSuppression(enabled) {
    _pendingNoiseSuppression = enabled;
    if (_activeEffect) {
        _activeEffect.setNoiseSuppression(enabled);
    }
}
/**
 * Returns whether the RNNoise stage is currently engaged (true = denoising).
 *
 * @returns {boolean}
 */
export function getNkfAecNoiseSuppression() {
    return _activeEffect ? Boolean(_activeEffect._nsEnabled) : _pendingNoiseSuppression;
}
// 音量の自動調整(メイクアップゲイン)。既定 ON。ノイズ抑制と同じく、生きている
// エフェクトへ即反映しつつ、あとで作られるエフェクトにも引き継ぐ。
/* ★2026-10-09 既定 OFF。後段の自動利得は消し残りのエコーまで 5〜6 倍にして相手へ返していた。
   ツールバーの「音量の自動調整」で ON にできる（調整経路は残す）。 */
let _pendingMakeupGain = false;
/**
 * 音量の自動調整を入切する。
 *
 * @param {boolean} enabled - true で自動調整を有効化。
 * @returns {void}
 */
export function setNkfAecMakeupGain(enabled) {
    _pendingMakeupGain = enabled;
    if (_activeEffect) {
        _activeEffect.setMakeupGain(enabled);
    }
}
/**
 * 音量の自動調整が有効かどうかを返す。
 *
 * @returns {boolean}
 */
export function getNkfAecMakeupGain() {
    return _activeEffect ? Boolean(_activeEffect._agcActive) : _pendingMakeupGain;
}
// 送信メーター(?nkfMeter=1 と同じもの)の表示。既定 OFF。
// URL パラメータは入室時に書き換えられて消えるので、ツールバーから開けるようにする。
let _pendingTxMeter = false;
/**
 * 送信メーターを開閉する。
 *
 * @param {boolean} on - true で表示。
 * @returns {void}
 */
export function setNkfTxMeter(on) {
    _pendingTxMeter = on;
    if (_activeEffect) {
        _activeEffect.setTransmitMeter(on);
    }
}
/**
 * 送信メーターが表示されているかどうか。
 *
 * @returns {boolean}
 */
export function getNkfTxMeter() {
    return _activeEffect ? Boolean(_activeEffect._meterWanted) : _pendingTxMeter;
}
// Returns true once the effect is loaded AND startEffect has been called (worklet running).
export function isNkfAecEffectReady() {
    return _activeEffect != null && _activeEffect._workletNode != null;
}
/**
 * Factory that builds and pre-loads an NkfAecEffect, hooked up to the
 * page-wide RemoteFarEndMix as its far-end reference.
 *
 * Returns undefined if config.nkfAec.enabled is not true so the caller can
 * fold this into Promise.all([...]) alongside other effects.
 */
export async function createNkfAecEffect(store) {
    const state = store.getState();
    const cfg = state['features/base/config'].nkfAec;
    if (!cfg?.enabled) {
        return undefined;
    }
    if (state['features/base/config'].disableAEC !== true) {
        logger.warn('nkfAec.enabled is true but config.disableAEC is not true — '
            + 'the browser\'s WebRTC AEC will run in front of NKF-aec and '
            + 'destroy near-end voice during double-talk.  '
            + 'Set "disableAEC: true" in config.js when enabling nkfAec.');
    }
    const modelUrl = cfg.modelUrl ?? '/libs/nkf-aec/models/nkf_kgnet.onnx';
    const workletPath = cfg.workletPath ?? '/libs/nkf-aec/sync-aec-worklet.js';
    // Reuse the last converged delay ONLY while the microphone is unchanged.
    //
    // A camera toggle re-runs getUserMedia on the SAME mic, and carrying the
    // delay over is what stops cancellation from "collapsing" on video ON/OFF.
    // A mic CHANGE is the opposite case: the speaker→mic path is different, so
    // the old delay points at the wrong place and the Worker's adaptive filter
    // is tuned to the wrong acoustics. Reusing them there meant the echo never
    // lined up again — cancellation failed every time after switching mics on
    // the prejoin screen, where there is no remote audio to re-converge from.
    const micId = String(getUserSelectedMicDeviceId(state) ?? '');
    const micChanged = micId !== _lastConvergedMicId;
    if (micChanged && _lastConvergedMicId) {
        logger.info(`NKF-aec: microphone changed (${_lastConvergedMicId} → ${micId}) `
            + '— dropping the previous delay and clearing the adaptive filter');
        _lastConvergedDelaySamples = 0;
    }
    const initialDelay = (micChanged ? 0 : _lastConvergedDelaySamples) || (cfg.farDelaySamples ?? 0);
    // Withhold the mic until cancellation is judged working (config
    // nkfAec.holdUntilReady) — but only on a FRESH start.
    //
    // A camera toggle rebuilds the effect on the same mic and re-seeds it with
    // the delay that already converged, so cancellation is in effect from the
    // first chunk. Holding again there would mute the user for several seconds
    // every time they turn the camera on or off, for no gain. A mic change
    // drops that delay (above), so it counts as fresh and does hold.
    const reusingConvergedDelay = !micChanged && _lastConvergedDelaySamples > 0;
    const holdUntilReady = cfg.holdUntilReady === true && !reusingConvergedDelay;
    if (cfg.holdUntilReady === true) {
        logger.info(`NKF-aec: hold-until-ready ${holdUntilReady ? 'ARMED' : 'skipped (rebuild reusing a converged delay)'}`);
    }
    // Wipe the shared engine's state before the new effect starts feeding it.
    // No-op when no shared Worker exists yet (first join).
    // 調査用: 共有エンジンは1個しかないので、ここで作り直すと「いま送信中の
    // エフェクト」の適応状態まで一緒に消える。どちらに転んだかを残す。
    console.info(`[nkf-dbg] createNkfAecEffect: micChanged=${micChanged}`
        + ` initialDelay=${initialDelay} holdUntilReady=${holdUntilReady}`
        + ` → 共有エンジンを${micChanged ? '作り直します' : '維持します'}`);
    if (micChanged) {
        await resetSharedAecEngine(modelUrl, initialDelay);
    }
    // From here on the engine belongs to this microphone. Recording it now
    // (rather than when a delay converges) keeps the comparison meaningful even
    // when convergence never happens — otherwise every later rebuild would look
    // like a mic change and reset a filter that had done nothing wrong.
    _lastConvergedMicId = micId;
    // Seed the RNNoise stage from the user's last choice: the module-level
    // pending flag, falling back to the persisted noise-suppression redux state.
    const nsEnabled = _pendingNoiseSuppression || Boolean(state['features/noise-suppression']?.enabled);
    const effect = new NkfAecEffect({
        modelUrl,
        workletPath,
        farDelaySamples: initialDelay,
        autoDelayTrack: cfg.autoDelayTrack,
        noiseSuppression: nsEnabled,
        makeupGainActive: _pendingMakeupGain,
        txMeter: _pendingTxMeter,
        holdUntilReady,
        readyGraceMs: cfg.readyGraceMs,
        dropoutWarnPct: cfg.dropoutWarnPct,
        dropoutClearPct: cfg.dropoutClearPct,
        // Tell the user when a meaningful share of their audio is not reaching
        // the other side. They cannot see the counters, and the far end usually
        // will not interrupt to say "you are breaking up", so a call can run for
        // minutes before anyone works out what is wrong.
        //
        // Only on the way in, and at most once every few minutes: the recovery
        // needs no announcement, and repeating the warning while the condition
        // persists helps nobody.
        onDropout: (on, pct) => {
            if (!on || cfg.dropoutWarning === false) {
                return;
            }
            const now = Date.now();
            if (now - _lastDropoutNotifyMs < DROPOUT_NOTIFY_COOLDOWN_MS) {
                return;
            }
            _lastDropoutNotifyMs = now;
            try {
                store.dispatch(showNotification({
                    title: '音声が途切れています',
                    description: `送信中の音声の約${Math.round(pct)}%が届いていません。`
                        + '他のアプリやタブを閉じると改善することがあります。'
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            catch (e) {
                logger.warn('NKF-aec: failed to show the dropout notification', e);
            }
        },
        // Persist every (converged) delay so the NEXT effect built after a
        // camera-toggle-induced mic re-gUM starts already aligned.
        // Persist every (converged) delay so the NEXT effect built for this same
        // microphone starts already aligned (camera toggle → mic re-gUM).
        onDelayChange: (d) => { _lastConvergedDelaySamples = d; },
        // Surface "is echo cancellation actually working yet?" to the toolbar.
        // OBSERVATION ONLY: no audio path reads this. It is here so a real
        // meeting can show whether the "preparing" window lines up with the
        // echo loops users report right after joining, before we act on it.
        // Delay settled, mic still held silent for the grace period. Reported
        // separately so the UI can distinguish it from "nothing known yet";
        // both are silence to the far side, but only one of them is progress.
        onSettling: (reason) => {
            try {
                store.dispatch(setNkfAecSettling(reason));
            }
            catch (e) {
                logger.warn('NKF-aec: failed to report settling', e);
            }
        },
        onReady: (working, reason) => {
            try {
                store.dispatch(setNkfAecReady(working, reason));
            }
            catch (e) {
                logger.warn('NKF-aec: failed to report readiness', e);
            }
        }
    });
    const mix = getRemoteFarEndMix(store);
    void mix.resume();
    effect.setFarEndSource(mix.getFarEndNode());
    logger.info(`Loading NKF-aec engine (initial farDelaySamples=${initialDelay})`);
    await effect.load();
    _activeEffect = effect;
    effect._userBypass = _pendingUserBypass;
    _pendingNoiseSuppression = nsEnabled;
    logger.info(`NKF-aec effect ready (RNNoise ${nsEnabled ? 'ON' : 'OFF'})`);
    // One-shot join calibration: play a short chime through the AEC far path on
    // the first mic-on and set the echo delay immediately, so cancellation works
    // from the start instead of waiting for the passive tracker to converge.
    // Low-confidence / disabled → silently defers to the passive tracker.
    if (cfg.autoCalibrate !== false && cfg.voiceUrl) {
        try {
            runCalibrationOnce(store, effect, {
                voiceUrl: cfg.voiceUrl,
                volume: cfg.voiceVolume ?? 0.35
            });
        }
        catch (e) {
            logger.warn('NKF-aec: failed to start join calibration', e);
        }
    }
    return effect;
}
